import React, { useState, useEffect } from 'react';
import { useStore } from '../context/StoreContext';
import { ProductCard } from './ProductCard';
import { SkeletonGrid } from './Skeleton';
import { 
  Smartphone, 
  Palette, 
  Frame,
  Shirt, 
  Sparkles, 
  Coffee, 
  ArrowRight, 
  Flame,
  Hammer,
  Key,
  Tag,
  X,
  ArrowUpDown,
  BookOpen,
  ShieldCheck,
  Truck,
  MessageCircle,
  CheckCircle2,
  Globe
} from 'lucide-react';
import { Product } from '../types';
import {
  getCategoryLocalizedName,
  getProductLocalizedTitle,
  getProductLocalizedDescription,
  getProductLocalizedTag,
  getProductPermalink,
  getCategoryPermalink,
  getLocalizedPath
} from '../utils/localization';
import { CategoryTagsMenu } from './CategoryTagsMenu';

type SortMode = 'blend' | 'newest' | 'oldest' | 'price-asc' | 'price-desc';

/**
 * Smart Catalog Distribution:
 * Interleaves recently added drops with original catalog classics and staple products,
 * so visitors immediately discover both new items and time-tested designs together at once.
 */
const blendNewAndOld = (items: Product[]): Product[] => {
  if (items.length <= 2) return items;
  const half = Math.ceil(items.length / 2);
  const newest = items.slice(0, half);
  const oldest = items.slice(half).reverse(); // oldest first
  const result: Product[] = [];
  const maxLen = Math.max(newest.length, oldest.length);
  for (let i = 0; i < maxLen; i++) {
    if (i < newest.length) result.push(newest[i]);
    if (i < oldest.length) result.push(oldest[i]);
  }
  return result;
};

export const FrontPageShowcase: React.FC = () => {
  const { 
    products, 
    isInitialLoading,
    categories, 
    activeCategoryFilter, 
    setActiveCategoryFilter, 
    setSelectedProduct,
    activeTagFilter,
    setActiveTagFilter,
    currentView,
    setCurrentView,
    searchQuery,
    savedCustomTags,
    language,
    setLanguage,
    t
  } = useStore();

  const handleSelectCategory = (cat: string) => {
    setSelectedProduct(null);
    setActiveCategoryFilter(cat);
  };

  const handleCategoryLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, cat: string) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    e.preventDefault();
    handleSelectCategory(cat);
  };

  const [isLoadingGrid, setIsLoadingGrid] = useState(true);
  const [visibleCount, setVisibleCount] = useState(12);
  const [sortMode, setSortMode] = useState<SortMode>('blend');

  // Smooth skeleton transition when category, tag, search, or sort changes
  useEffect(() => {
    setIsLoadingGrid(true);
    setVisibleCount(12);
    const timer = setTimeout(() => {
      setIsLoadingGrid(false);
    }, 240);
    return () => clearTimeout(timer);
  }, [activeCategoryFilter, activeTagFilter, searchQuery, sortMode]);

  // Inject Catalog Schema.org Product ItemList for SEO
  useEffect(() => {
    if (typeof document === 'undefined' || products.length === 0) return;

    const scriptId = 'catalog-products-jsonld';
    let scriptEl = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.id = scriptId;
      scriptEl.type = 'application/ld+json';
      document.head.appendChild(scriptEl);
    }

    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://coveristic.net';
    const activeItems = products.filter(p => !p.inRecycleBin && !p.unlisted);

    const catalogSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      'name': 'Coveristic Custom Print on Demand Products',
      'description': 'Custom phone cases, canvas wall art, heavyweight apparel, photo frames, and ceramic mugs in Lebanon.',
      'numberOfItems': activeItems.length,
      'itemListElement': activeItems.slice(0, 50).map((prod, idx) => ({
        '@type': 'ListItem',
        'position': idx + 1,
        'item': {
          '@type': 'Product',
          'name': getProductLocalizedTitle(prod, language),
          'image': prod.image.startsWith('http') ? prod.image : `${origin}${prod.image.startsWith('/') ? '' : '/'}${prod.image}`,
          'description': getProductLocalizedDescription(prod, language),
          'sku': prod.id,
          'category': prod.category,
          'offers': {
            '@type': 'Offer',
            'url': `${origin}${getProductPermalink(prod, language)}`,
            'priceCurrency': 'USD',
            'price': prod.price,
            'availability': prod.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
            'seller': {
              '@type': 'Organization',
              '@id': 'https://coveristic.net/#organization',
              'name': 'Coveristic',
              'url': 'https://coveristic.net/',
              'telephone': '+96181773349',
              'email': 'info@coveristic.net'
            }
          }
        }
      }))
    };

    scriptEl.textContent = JSON.stringify(catalogSchema);

    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, [products, language]);

  // Filter products by category, custom tag, and search (exclude recycle bin and unlisted items)
  const activeProducts = products.filter(p => !p.inRecycleBin && !p.unlisted);

  const matchedSavedTagInStorefront = React.useMemo(() => {
    const q = searchQuery ? searchQuery.trim().toLowerCase() : '';
    if (!q) return null;
    return (savedCustomTags || []).find(st => st.text.toLowerCase() === q || st.text.toLowerCase().includes(q)) || null;
  }, [searchQuery, savedCustomTags]);

  const filteredProducts = React.useMemo(() => {
    return activeProducts.filter((p) => {
      const matchesCategory = activeCategoryFilter === 'all' || p.category === activeCategoryFilter;
      const matchesTag = !activeTagFilter || (
        (p.customTag?.text && p.customTag.text.toLowerCase() === activeTagFilter.toLowerCase()) ||
        p.tags.some(t => t.toLowerCase() === activeTagFilter.toLowerCase())
      );
      const query = searchQuery ? searchQuery.trim().toLowerCase() : '';
      const arTitle = getProductLocalizedTitle(p, 'ar').toLowerCase();
      const arDesc = getProductLocalizedDescription(p, 'ar').toLowerCase();
      const matchesSearch = !query || 
        p.title.toLowerCase().includes(query) ||
        arTitle.includes(query) ||
        arDesc.includes(query) ||
        (p.customTag?.text && p.customTag.text.toLowerCase().includes(query)) ||
        p.tags.some(t => t.toLowerCase().includes(query) || getProductLocalizedTag(t, 'ar').toLowerCase().includes(query)) ||
        p.description.toLowerCase().includes(query);
      return matchesCategory && matchesTag && matchesSearch;
    });
  }, [activeProducts, activeCategoryFilter, activeTagFilter, searchQuery]);

  // Compute displayed products according to the selected sort mode
  // Default 'blend': balances both recently added drops and timeless catalog classics so visitors see both at once!
  const displayProducts = React.useMemo(() => {
    if (sortMode === 'blend') {
      return blendNewAndOld(filteredProducts);
    }
    if (sortMode === 'newest') {
      return [...filteredProducts];
    }
    if (sortMode === 'oldest') {
      return [...filteredProducts].reverse();
    }
    if (sortMode === 'price-asc') {
      return [...filteredProducts].sort((a, b) => a.price - b.price);
    }
    if (sortMode === 'price-desc') {
      return [...filteredProducts].sort((a, b) => b.price - a.price);
    }
    return filteredProducts;
  }, [filteredProducts, sortMode]);

  // Featured collections: blend new and classic items so customers discover older staples alongside new drops
  const phoneCases = React.useMemo(() => blendNewAndOld(activeProducts.filter(p => p.category === 'phone-cases')), [activeProducts]);
  const canvasArt = React.useMemo(() => blendNewAndOld(activeProducts.filter(p => p.category === 'canvas')), [activeProducts]);
  const photoFrames = React.useMemo(() => blendNewAndOld(activeProducts.filter(p => p.category === 'photo-frames')), [activeProducts]);
  const woodCrafts = React.useMemo(() => blendNewAndOld(activeProducts.filter(p => p.category === 'wood-crafts')), [activeProducts]);
  const keychains = React.useMemo(() => blendNewAndOld(activeProducts.filter(p => p.category === 'keychains')), [activeProducts]);
  const mugs = React.useMemo(() => blendNewAndOld(activeProducts.filter(p => p.category === 'mugs')), [activeProducts]);
  const apparel = React.useMemo(() => blendNewAndOld(activeProducts.filter(p => p.category === 'tshirts' || p.category === 'hoodies')), [activeProducts]);
  const stickers = React.useMemo(() => blendNewAndOld(activeProducts.filter(p => p.category === 'stickers')), [activeProducts]);

  return (
    <div className="space-y-16 sm:space-y-24 py-10 sm:py-16">
      
      {/* 1. Category Quick Navigation Ribbon */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between pb-4 border-b border-neutral-200 mb-6">
          <div>
            <span className="text-[10px] font-black uppercase tracking-widest text-[#E21B24]">
              {language === 'ar' ? 'طباعة حسب الطلب' : 'CUSTOM PRINT ON DEMAND'}
            </span>
            <h2 className="text-xl sm:text-2xl font-black font-heading uppercase text-neutral-900">
              {language === 'ar' ? 'اختر حسب نوع المنتج' : 'Personalize by Product'}
            </h2>
          </div>
          <a
            href={getLocalizedPath('/shop', language)}
            onClick={(e) => {
              if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey && e.button === 0) {
                e.preventDefault();
                setActiveCategoryFilter('all');
                setCurrentView('shop');
              }
            }}
            className="text-xs font-black uppercase tracking-wider text-neutral-900 hover:text-[#E21B24] flex items-center gap-1 transition-colors"
          >
            <span>{language === 'ar' ? 'استكشف الكتالوج الكامل' : 'Explore Full Catalog'}</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>

        {/* Category Pills Bar (Distinct Anchor Texts for SEO Internal Link Uniqueness) */}
        <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-2 scrollbar-none">
          <a
            href={getLocalizedPath('/shop', language)}
            onClick={(e) => handleCategoryLinkClick(e, 'all')}
            style={{ fontWeight: 500, fontStyle: 'normal' }}
            className={`px-4 py-2.5 rounded-sm text-xs font-medium normal-case tracking-normal whitespace-nowrap transition-all ${
              activeCategoryFilter === 'all'
                ? 'bg-[#111111] text-white shadow-sm'
                : 'bg-white text-neutral-800 border border-neutral-200 hover:border-neutral-900'
            }`}
          >
            {t.allCategories}
          </a>

          <a
            href={getCategoryPermalink('phone-cases', language)}
            onClick={(e) => handleCategoryLinkClick(e, 'phone-cases')}
            style={{ fontWeight: 500, fontStyle: 'normal' }}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-xs font-medium normal-case tracking-normal whitespace-nowrap transition-all ${
              activeCategoryFilter === 'phone-cases'
                ? 'bg-[#E21B24] text-white shadow-sm'
                : 'bg-white text-neutral-800 border border-neutral-200 hover:border-[#E21B24]'
            }`}
          >
            <Smartphone className="w-3.5 h-3.5" />
            <span>{language === 'ar' ? 'تصفح كفرات الهواتف' : 'Shop Phone Cases'}</span>
          </a>

          <a
            href={getCategoryPermalink('canvas', language)}
            onClick={(e) => handleCategoryLinkClick(e, 'canvas')}
            style={{ fontWeight: 500, fontStyle: 'normal' }}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-xs font-medium normal-case tracking-normal whitespace-nowrap transition-all ${
              activeCategoryFilter === 'canvas'
                ? 'bg-[#E21B24] text-white shadow-sm'
                : 'bg-white text-neutral-800 border border-neutral-200 hover:border-[#E21B24]'
            }`}
          >
            <Palette className="w-3.5 h-3.5" />
            <span>{language === 'ar' ? 'تصفح لوحات الكانفاس' : 'Shop Wall Canvas'}</span>
          </a>

          <a
            href={getCategoryPermalink('photo-frames', language)}
            onClick={(e) => handleCategoryLinkClick(e, 'photo-frames')}
            style={{ fontWeight: 500, fontStyle: 'normal' }}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-xs font-medium normal-case tracking-normal whitespace-nowrap transition-all ${
              activeCategoryFilter === 'photo-frames'
                ? 'bg-[#E21B24] text-white shadow-sm'
                : 'bg-white text-neutral-800 border border-neutral-200 hover:border-[#E21B24]'
            }`}
          >
            <Frame className="w-3.5 h-3.5" />
            <span>{language === 'ar' ? 'تصفح إطارات الصور' : 'Shop Photo Frames'}</span>
          </a>

          <a
            href={getCategoryPermalink('mugs', language)}
            onClick={(e) => handleCategoryLinkClick(e, 'mugs')}
            style={{ fontWeight: 500, fontStyle: 'normal' }}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-xs font-medium normal-case tracking-normal whitespace-nowrap transition-all ${
              activeCategoryFilter === 'mugs'
                ? 'bg-[#E21B24] text-white shadow-sm'
                : 'bg-white text-neutral-800 border border-neutral-200 hover:border-[#E21B24]'
            }`}
          >
            <Coffee className="w-3.5 h-3.5" />
            <span>{language === 'ar' ? 'تصفح الأكواب المطبوعة' : 'Shop Ceramic Mugs'}</span>
          </a>

          <a
            href={getCategoryPermalink('tshirts', language)}
            onClick={(e) => handleCategoryLinkClick(e, 'tshirts')}
            style={{ fontWeight: 500, fontStyle: 'normal' }}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-xs font-medium normal-case tracking-normal whitespace-nowrap transition-all ${
              activeCategoryFilter === 'tshirts'
                ? 'bg-[#E21B24] text-white shadow-sm'
                : 'bg-white text-neutral-800 border border-neutral-200 hover:border-[#E21B24]'
            }`}
          >
            <Shirt className="w-3.5 h-3.5" />
            <span>{language === 'ar' ? 'تصفح التيشيرتات القطنية' : 'Shop Cotton T-Shirts'}</span>
          </a>

          <a
            href={getCategoryPermalink('hoodies', language)}
            onClick={(e) => handleCategoryLinkClick(e, 'hoodies')}
            style={{ fontWeight: 500, fontStyle: 'normal' }}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-xs font-medium normal-case tracking-normal whitespace-nowrap transition-all ${
              activeCategoryFilter === 'hoodies'
                ? 'bg-[#E21B24] text-white shadow-sm'
                : 'bg-white text-neutral-800 border border-neutral-200 hover:border-[#E21B24]'
            }`}
          >
            <Shirt className="w-3.5 h-3.5" />
            <span>{language === 'ar' ? 'تصفح سترات الهودي' : 'Shop Fleece Hoodies'}</span>
          </a>

          <a
            href={getCategoryPermalink('stickers', language)}
            onClick={(e) => handleCategoryLinkClick(e, 'stickers')}
            style={{ fontWeight: 500, fontStyle: 'normal' }}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-xs font-medium normal-case tracking-normal whitespace-nowrap transition-all ${
              activeCategoryFilter === 'stickers'
                ? 'bg-[#E21B24] text-white shadow-sm'
                : 'bg-white text-neutral-800 border border-neutral-200 hover:border-[#E21B24]'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>{language === 'ar' ? 'تصفح ملصقات الفينيل' : 'Shop Vinyl Stickers'}</span>
          </a>
        </div>
      </section>

      {/* 2. Active Filter Product Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-2.5">
            <span className="w-3 h-3 rounded-full bg-[#E21B24]"></span>
            {currentView === 'shop' ? (
              <h1 id="featured-products-heading" className="text-2xl sm:text-3xl font-black font-heading uppercase text-neutral-900">
                {activeCategoryFilter === 'all' 
                  ? (language === 'ar' ? 'المنتجات المميزة — طباعة حسب الطلب في لبنان' : 'Custom Print Products Catalog — Coveristic Lebanon')
                  : (language === 'ar' ? `تشكيلة ${getCategoryLocalizedName(activeCategoryFilter, language)}` : `Custom ${activeCategoryFilter.replace('-', ' ')} Collection Lebanon`)}
              </h1>
            ) : (
              <h2 id="featured-products-heading" className="text-2xl sm:text-3xl font-black font-heading uppercase text-neutral-900">
                {activeCategoryFilter === 'all' 
                  ? (language === 'ar' ? 'المنتجات المميزة' : 'Featured Products')
                  : (language === 'ar' ? `تشكيلة ${getCategoryLocalizedName(activeCategoryFilter, language)}` : `${activeCategoryFilter.replace('-', ' ')} Collection`)}
              </h2>
            )}
            <span className="text-xs sm:text-sm text-neutral-500 font-bold ml-1">
              {language === 'ar' ? `(${filteredProducts.length} منتج)` : `(${filteredProducts.length} items)`}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {/* Compact Switch: English Products vs Arabic Products (/ar/) */}
            <div
              id="products-language-switch"
              className="inline-flex items-center bg-neutral-100 border border-neutral-300 rounded-sm p-0.5 text-xs shadow-2xs"
              title="Switch between English version products and Arabic version products (/ar/)"
            >
              <Globe className="w-3.5 h-3.5 text-[#E21B24] mx-1.5 shrink-0" />
              <button
                type="button"
                onClick={() => setLanguage('en')}
                className={`px-2.5 py-1 rounded-xs font-black uppercase tracking-wider text-[11px] transition-all cursor-pointer ${
                  language === 'en'
                    ? 'bg-neutral-900 text-white shadow-2xs'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                EN Products
              </button>
              <button
                type="button"
                onClick={() => setLanguage('ar')}
                className={`px-2.5 py-1 rounded-xs font-black tracking-wide text-[11px] transition-all cursor-pointer ${
                  language === 'ar'
                    ? 'bg-[#E21B24] text-white shadow-2xs'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                المنتجات بالعربية (/ar/)
              </button>
            </div>

            {/* Active Tag Filter Pill with Clear */}
            {activeTagFilter && (
              <div className="flex items-center gap-2 bg-neutral-900 text-white px-3.5 py-2 rounded-xs text-xs sm:text-sm font-bold shadow-xs">
                <Tag className="w-4 h-4 text-[#E21B24]" />
                <span>{language === 'ar' ? 'الوسم:' : 'Tag:'} <span className="text-amber-300 uppercase">{activeTagFilter}</span></span>
                {activeCategoryFilter !== 'all' && (
                  <span className="text-neutral-400">
                    {language === 'ar' ? `في ${getCategoryLocalizedName(activeCategoryFilter, language)}` : `in ${activeCategoryFilter.replace('-', ' ')}`}
                  </span>
                )}
                <button
                  onClick={() => setActiveTagFilter(null)}
                  className="ml-1.5 p-0.5 hover:bg-neutral-800 rounded text-neutral-300 hover:text-white cursor-pointer transition-colors"
                  title="Clear tag filter"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Smart Catalog Sort Control */}
            <div className="flex items-center gap-1.5 bg-white border border-neutral-300 hover:border-neutral-500 rounded-sm px-3 py-2 text-xs shadow-2xs transition-colors">
              <ArrowUpDown className="w-3.5 h-3.5 text-[#E21B24] shrink-0" />
              <label htmlFor="frontpage-sort-select" className="text-neutral-500 font-bold text-[11px] whitespace-nowrap">
                {t.sortByLabel || (language === 'ar' ? 'ترتيب:' : 'Sort:')}
              </label>
              <select
                id="frontpage-sort-select"
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value as SortMode)}
                className="bg-transparent font-black text-neutral-900 text-xs focus:outline-none cursor-pointer pr-1"
                aria-label="Sort products"
              >
                <option value="blend">{t.sortOptionBlend || '✨ New & Classics Blend'}</option>
                <option value="newest">{t.sortOptionNewest || '🆕 Newest Releases'}</option>
                <option value="oldest">{t.sortOptionOldest || '⭐ Original Classics'}</option>
                <option value="price-asc">{t.sortByPriceLowHigh || 'Price: Low to High'}</option>
                <option value="price-desc">{t.sortByPriceHighLow || 'Price: High to Low'}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Small Tags Menu Component: Enables visitors to quickly view products by tags in every category page */}
        <CategoryTagsMenu className="mb-6" />

        {isLoadingGrid || (isInitialLoading && products.length === 0) ? (
          <SkeletonGrid count={8} />
        ) : filteredProducts.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-sm p-12 text-center max-w-md mx-auto space-y-4">
            <div className="w-12 h-12 bg-neutral-100 rounded-full flex items-center justify-center mx-auto text-neutral-400">
              <Sparkles className="w-6 h-6" />
            </div>
            {matchedSavedTagInStorefront ? (
              <div className="space-y-3">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded text-xs font-black uppercase text-white shadow-xs" style={{ backgroundColor: matchedSavedTagInStorefront.color }}>
                  <Tag className="w-3.5 h-3.5" />
                  <span>{matchedSavedTagInStorefront.text}</span>
                </div>
                <h3 className="font-bold text-neutral-900">
                  {language === 'ar' ? `الوسم "${matchedSavedTagInStorefront.text}" مسجل` : `Tag "${matchedSavedTagInStorefront.text}" exists`}
                </h3>
                <p className="text-xs text-neutral-500">
                  {language === 'ar' 
                    ? 'هذا الوسم مسجل في دليل منتجاتنا، ولكن لا توجد منتجات مخصصة له حالياً.'
                    : 'This tag is registered in our catalog, but there are currently no published products assigned to it yet.'}
                </p>
              </div>
            ) : (
              <>
                <h3 className="font-bold text-neutral-900">{language === 'ar' ? 'لم يتم العثور على أي منتج' : 'No products found'}</h3>
                <p className="text-xs text-neutral-500">
                  {activeTagFilter 
                    ? (language === 'ar' ? `لا توجد منتجات مطابقة للوسم "${activeTagFilter}".` : `No products found matching tag "${activeTagFilter}" in ${activeCategoryFilter === 'all' ? 'the catalog' : activeCategoryFilter.replace('-', ' ')}.`)
                    : (language === 'ar' ? 'يرجى تجربة كلمة بحث أخرى أو تصفح الفئات أعلاه.' : 'Try adjusting your search query or browse our featured custom categories above.')}
                </p>
              </>
            )}
            <button
              onClick={() => {
                setActiveCategoryFilter('all');
                setActiveTagFilter(null);
              }}
              className="bg-[#E21B24] text-white text-xs font-bold uppercase px-4 py-2 rounded cursor-pointer"
            >
              {language === 'ar' ? 'إعادة ضبط الفلاتر' : 'Reset Filters'}
            </button>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {displayProducts.slice(0, visibleCount).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {filteredProducts.length > visibleCount && (
              <div className="mt-10 sm:mt-12 flex flex-col items-center justify-center gap-2.5">
                <button
                  type="button"
                  onClick={() => setVisibleCount((prev) => prev + 12)}
                  className="bg-[#111111] hover:bg-black text-white text-xs sm:text-sm font-black uppercase tracking-wider px-10 py-4 rounded-sm shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all inline-flex items-center gap-2.5 cursor-pointer"
                >
                  <span>{language === 'ar' ? 'تحميل المزيد من المنتجات' : 'Load More Products'}</span>
                  <span className="text-neutral-400 font-normal">
                    {language === 'ar' ? `(باقي ${filteredProducts.length - visibleCount})` : `(${filteredProducts.length - visibleCount} more)`}
                  </span>
                </button>
                <span className="text-xs text-neutral-400 font-medium">
                  {language === 'ar' 
                    ? `عرض ${Math.min(visibleCount, filteredProducts.length)} من أصل ${filteredProducts.length} منتج` 
                    : `Showing ${Math.min(visibleCount, filteredProducts.length)} of ${filteredProducts.length} items`}
                </span>
              </div>
            )}
          </div>
        )}
      </section>

      {/* 3. Featured Collection: Phone Cases Spotlight */}
      <section className="bg-white border-y border-neutral-200 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-[#E21B24] bg-red-50 px-2.5 py-0.5 rounded border border-red-100 mb-2">
                <Flame className="w-3.5 h-3.5 fill-[#E21B24]" />
                <span>{language === 'ar' ? 'المنتج الأكثر طلباً' : 'POPULAR CUSTOM PRODUCT'}</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-black font-heading uppercase text-neutral-900">
                {language === 'ar' ? 'كفرات هواتف حسب الطلب' : 'Custom Phone Cases'}
              </h2>
              <p className="text-neutral-500 text-xs sm:text-sm mt-1 max-w-xl">
                {language === 'ar' 
                  ? 'اطبع صورك ورسوماتك المفضلة وذكرياتك على كفرات هواتف مخصصة لكافة الموديلات في لبنان. بروفة ومعاينة مجانية متوفرة قبل البدء بالطباعة.'
                  : 'Print your photos, pet portraits, couple moments, and custom designs onto custom phone cases for almost all phone brands in Lebanon. Image preview before printing request is available before printing.'}
              </p>
            </div>

            <a
              href={getCategoryPermalink('phone-cases', language)}
              onClick={(e) => {
                if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey && e.button === 0) {
                  e.preventDefault();
                  setActiveCategoryFilter('phone-cases');
                  window.scrollTo({ top: 350, behavior: 'smooth' });
                }
              }}
              className="bg-[#111111] hover:bg-black text-white text-xs font-black uppercase px-5 py-2.5 rounded-sm tracking-wider transition-all self-start md:self-auto"
            >
              {language === 'ar' ? 'عرض كافة كفرات الهواتف' : 'View All Phone Cases'}
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {phoneCases.slice(0, 4).map((caseProduct) => (
              <ProductCard key={caseProduct.id} product={caseProduct} />
            ))}
          </div>
        </div>
      </section>

      {/* 4. Featured Collection: Canvas Wall Art Spotlight */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-[#E21B24]">
              {language === 'ar' ? 'أجمل الذكريات على جدران منزلك' : 'CHERISHED MEMORIES ON WALLS'}
            </span>
            <h2 className="text-2xl sm:text-3xl font-black font-heading uppercase text-neutral-900 mt-1">
              {language === 'ar' ? 'لوحات كانفاس جدارية' : 'Custom Wall Art Canvas'}
            </h2>
            <p className="text-neutral-500 text-xs sm:text-sm mt-1 max-w-xl">
              {language === 'ar'
                ? 'حوّل صور زفافك ولحظاتك العائلية ومناظرك المفضلة إلى لوحات كانفاس جدارية مميزة في لبنان.'
                : 'Turn your wedding pictures, family milestones, travel panoramas, and digital art into custom Canvas wall art in Lebanon.'}
            </p>
          </div>

          <a
            href={getCategoryPermalink('canvas', language)}
            onClick={(e) => {
              if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey && e.button === 0) {
                e.preventDefault();
                setActiveCategoryFilter('canvas');
                window.scrollTo({ top: 350, behavior: 'smooth' });
              }
            }}
            className="bg-[#111111] hover:bg-black text-white text-xs sm:text-sm font-black uppercase px-5 py-2.5 rounded-sm tracking-wider transition-all self-start md:self-auto"
          >
            {language === 'ar' ? 'عرض كافة لوحات الكانفاس' : 'View All Canvas Prints'}
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {canvasArt.slice(0, 3).map((canvas) => (
            <ProductCard key={canvas.id} product={canvas} />
          ))}
        </div>
      </section>

      {/* 5. Featured Collection: Photo Frames (A4 & A3) Spotlight */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-[#E21B24]">
              {language === 'ar' ? '★ منتج جديد مميز' : '★ NEW PRODUCT DROP'}
            </span>
            <h2 className="text-2xl sm:text-3xl font-black font-heading uppercase text-neutral-900 mt-1">
              {language === 'ar' ? 'إطارات صور مخصصة' : 'Custom Photo Frames'}
            </h2>
            <p className="text-neutral-500 text-xs sm:text-sm mt-1 max-w-xl">
              {language === 'ar'
                ? 'احتفظ بصور تخرجك وزفافك وأجمل ذكرياتك داخل إطارات خشبية وزجاجية فاخرة تناسب المكاتب والجدران.'
                : 'Preserve your graduation, wedding, family, and art prints in gallery-grade wooden & modern frames with crystal glass.'}
            </p>
          </div>

          <a
            href={getCategoryPermalink('photo-frames', language)}
            onClick={(e) => {
              if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey && e.button === 0) {
                e.preventDefault();
                setActiveCategoryFilter('photo-frames');
                window.scrollTo({ top: 350, behavior: 'smooth' });
              }
            }}
            className="bg-[#111111] hover:bg-black text-white text-xs sm:text-sm font-black uppercase px-5 py-2.5 rounded-sm tracking-wider transition-all self-start md:self-auto"
          >
            {language === 'ar' ? 'عرض كافة إطارات الصور' : 'View All Photo Frames'}
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {photoFrames.slice(0, 3).map((frame) => (
            <ProductCard key={frame.id} product={frame} />
          ))}
        </div>
      </section>

      {/* 6. Editorial SEO Guide: Custom Printing, Phone Cases & Canvas Art in Lebanon */}
      <section 
        id="storefront-seo-guide"
        className="max-w-7xl mx-auto px-4 sm:px-6 pt-6"
      >
        <div className="bg-white border border-neutral-200/90 rounded-2xl p-6 sm:p-10 shadow-xs space-y-8">
          {/* Section Header */}
          <div className="border-b border-neutral-100 pb-6 max-w-3xl">
            <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-[#E21B24] inline-block mb-1">
              {language === 'ar' ? '★ دليل الطباعة المخصصة في لبنان' : '★ LEBANON CUSTOM PRINTING & CASE STUDIO GUIDE'}
            </span>
            <h2 className="text-2xl sm:text-3xl font-black font-heading uppercase text-neutral-900 tracking-tight leading-tight">
              {language === 'ar'
                ? 'لماذا يعتبر كوفريستيك الخيار الأول لطباعة كفرات الموبايل والكانفاس في لبنان؟'
                : 'Why Coveristic is Lebanon’s Leading Custom Print & Phone Case Studio'}
            </h2>
            <p className="text-neutral-500 text-xs sm:text-sm mt-2 leading-relaxed">
              {language === 'ar'
                ? 'نقدّم حلول الطباعة الرقمية الفاخرة حسب الطلب بأعلى مواصفات الجودة العالمية، مع خدمة المعاينة الفورية عبر واتساب والتوصيل السريع والدفع عند الاستلام لكافة المناطق اللبنانية.'
                : 'Explore our high-performance custom print on demand studio engineered for Lebanese tastemakers, offering custom phone cases, canvas wall art, and effortless WhatsApp order confirmation with nationwide Cash on Delivery.'}
            </p>
          </div>

          {/* Core Feature Badges */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 rounded-xl bg-neutral-50 border border-neutral-200/70 space-y-2">
              <div className="w-9 h-9 rounded-lg bg-neutral-900 text-white flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-[#E21B24]" />
              </div>
              <h3 className="text-xs font-black uppercase tracking-wider text-neutral-900">
                {language === 'ar' ? 'كفرات هواتف متينة وأنيقة' : 'Sleek Everyday Phone Cases'}
              </h3>
              <p className="text-[11px] text-neutral-500 leading-relaxed">
                {language === 'ar'
                  ? 'كفرات مصممة بحواف بارزة لحماية الكاميرا والشاشة مع مسكة مريحة لجميع أجهزة iPhone وسامسونغ وشاومي.'
                  : 'Designed with a comfortable grip, precise cutouts, and a protective bezel for iPhone, Samsung, and Xiaomi devices.'}
              </p>
            </div>

            <div className="p-4 rounded-xl bg-neutral-50 border border-neutral-200/70 space-y-2">
              <div className="w-9 h-9 rounded-lg bg-neutral-900 text-white flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#E21B24]" />
              </div>
              <h3 className="text-xs font-black uppercase tracking-wider text-neutral-900">
                {language === 'ar' ? 'طباعة عالية الدقة 1200 DPI' : '1200 DPI High Definition Print'}
              </h3>
              <p className="text-[11px] text-neutral-500 leading-relaxed">
                {language === 'ar'
                  ? 'أحبار يابانية فائقة الثبات مقاومة لأشعة الشمس والخدوش تضمن بقاء ألوان صورك زاهية لسنوات طويلة.'
                  : 'Japanese UV and pigment inks ensure true-to-life color depth, scratch resistance, and zero peeling over years of daily handling.'}
              </p>
            </div>

            <div className="p-4 rounded-xl bg-neutral-50 border border-neutral-200/70 space-y-2">
              <div className="w-9 h-9 rounded-lg bg-neutral-900 text-white flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-[#25D366]" />
              </div>
              <h3 className="text-xs font-black uppercase tracking-wider text-neutral-900">
                {language === 'ar' ? 'معاينة مجانية عبر واتساب' : 'Free WhatsApp Digital Proof'}
              </h3>
              <p className="text-[11px] text-neutral-500 leading-relaxed">
                {language === 'ar'
                  ? 'أرسل صورتك لنجهّز لك نموذجاً رقمياً واقعياً لتشاهده وتعتمد التصميم قبل بدء مرحلة الطباعة النهائية.'
                  : 'Send any photo or digital design to WhatsApp +961 81 773 349 and receive an exact digital proof before printing begins.'}
              </p>
            </div>

            <div className="p-4 rounded-xl bg-neutral-50 border border-neutral-200/70 space-y-2">
              <div className="w-9 h-9 rounded-lg bg-neutral-900 text-white flex items-center justify-center">
                <Truck className="w-5 h-5 text-[#E21B24]" />
              </div>
              <h3 className="text-xs font-black uppercase tracking-wider text-neutral-900">
                {language === 'ar' ? 'توصيل ودفع عند الاستلام' : 'Cash on Delivery Across Lebanon'}
              </h3>
              <p className="text-[11px] text-neutral-500 leading-relaxed">
                {language === 'ar'
                  ? 'شحن سريع خلال 24 إلى 48 ساعة إلى بيروت وجميع المحافظات مع خيار الدفع نقداً بالدولار أو عبر Whish Money.'
                  : 'Fast 24 to 48-hour delivery to Beirut, Saida, Tripoli, Jounieh, Byblos, Tyre, and Bekaa with Cash on Delivery or Whish Money.'}
              </p>
            </div>
          </div>

          {/* Structured Editorial Paragraphs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4 border-t border-neutral-100 text-neutral-600 text-xs sm:text-sm leading-relaxed">
            {/* Column 1 */}
            <div className="space-y-5">
              <div className="space-y-2">
                <h3 className="text-sm sm:text-base font-black uppercase tracking-tight text-neutral-900 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#E21B24] shrink-0" />
                  <span>
                    {language === 'ar' 
                      ? 'كفرات موبايل مخصصة لكافة الموديلات في لبنان' 
                      : 'Custom Phone Cases for All Brands in Lebanon'}
                  </span>
                </h3>
                <p>
                  {language === 'ar'
                    ? 'في متجر Coveristic، ندمج بين الجمالية العصرية والتصميم الشخصي. تُصنع كفرات الموبايل المخصصة لدينا من مواد متينة عالية الجودة لحماية هاتفك اليومي، مع مقاسات دقيقة لجميع الأزرار والمنافذ والكاميرات.'
                    : 'At Coveristic, we fuse modern streetwear aesthetics with personalized design. Our custom phone cases in Lebanon are built with durable, high-quality materials to protect your daily phone with precision fits for all buttons, ports, and camera lenses.'}
                </p>
                <p>
                  {language === 'ar'
                    ? 'نوفر كفرات مخصصة لمعظم أجهزة Apple iPhone (من iPhone 11 وحتى أحدث iPhone 16 Pro Max)، وأجهزة Samsung Galaxy، وXiaomi، وRedmi، وPOCO، وHonor. يمكنك طباعة صورك الخاصة، أو رسوماتك المفضلة، أو شعار عملك بوضوح فائق وثبات دائم لا يتلاشى مع الاستخدام اليومي المستمر.'
                    : 'We support all major device models including Apple iPhone (iPhone 11 through 16 Pro Max), Samsung Galaxy S21 to S25 Ultra, Xiaomi, Redmi, POCO, and Honor. Upload personal photos, pet portraits, couple memories, or custom streetwear graphics printed with permanent scratch-resistant UV inks.'}
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="text-sm sm:text-base font-black uppercase tracking-tight text-neutral-900 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#E21B24] shrink-0" />
                  <span>
                    {language === 'ar' 
                      ? 'لوحات كانفاس وإطارات صور تزيّن منازلكم ومكاتبكم' 
                      : 'Custom Canvas Wall Art & Photo Frames'}
                  </span>
                </h3>
                <p>
                  {language === 'ar'
                    ? 'حوّل أجمل لحظات زفافك، أو تخرجك، أو صورك العائلية إلى لوحات كانفاس فاخرة. نستخدم قماش كانفاس عالي الجودة مطبوع بأحبار صبغية نقية خالية من الروائح، وجاهزة للتعليق الفوري على جدران منزلك أو مكتبك.'
                    : 'Transform your cherished wedding photography, graduation milestones, family portraits, and digital art into custom canvas wall art. We utilize high-quality canvas rendered with rich, vibrant pigment inks, ready to hang on the walls of your home or office.'}
                </p>
              </div>
            </div>

            {/* Column 2 */}
            <div className="space-y-5">
              <div className="space-y-2">
                <h3 className="text-sm sm:text-base font-black uppercase tracking-tight text-neutral-900 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#E21B24] shrink-0" />
                  <span>
                    {language === 'ar' 
                      ? 'أكواب سيراميك، هوديات، تيشيرتات وهدايا مميزة' 
                      : 'Custom Ceramic Mugs, Heavyweight Apparel & Unique Gifts'}
                  </span>
                </h3>
                <p>
                  {language === 'ar'
                    ? 'إلى جانب كفرات الموبايل والكانفاس، يقدّم استوديو Coveristic مجموعة واسعة من الهدايا المبتكرة التي تناسب جميع المناسبات في لبنان، بما في ذلك أكواب السيراميك الآمنة لغسالة الصحون والميكروويف، وتيشيرتات قطنية بوزن 240 غرام، وهوديات شتوية مريحة، بالإضافة إلى ستيكرات مقاومة للماء للابتوبات والسيارات.'
                    : 'Beyond protective custom phone cases and canvas prints, Coveristic crafts an extensive selection of personalized gifts for birthdays, anniversaries, and company branding across Lebanon. Discover dishwasher-safe ceramic mugs, heavyweight 240 GSM streetwear cotton t-shirts, fleece-lined winter hoodies, and waterproof UV vinyl stickers.'}
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="text-sm sm:text-base font-black uppercase tracking-tight text-neutral-900 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#E21B24] shrink-0" />
                  <span>
                    {language === 'ar' 
                      ? 'كيف تطلب من Coveristic بدون بطاقات بنكية؟' 
                      : 'Seamless WhatsApp Ordering with Cash on Delivery in Lebanon'}
                  </span>
                </h3>
                <p>
                  {language === 'ar'
                    ? 'نسهّل عليك تجربة الشراء بالكامل دون الحاجة لأي تعقيدات أو بطاقات ائتمانية. كل ما عليك فعله هو اختيار المنتج وإرسال صورتك عبر تطبيق واتساب (+961 81 773 349). نرسل لك بروفة للموافقة، ثم نطبع ونشحن طلبك مع التوصيل السريع لجميع مناطق لبنان وخيار الدفع نقداً عند الاستلام بالدولار ($ USD) أو عبر Whish Money.'
                    : 'Enjoy zero-hassle shopping without international credit card restrictions. Simply browse our catalog, click Order via WhatsApp, or text +961 81 773 349 with your photo. We provide a digital preview for your approval before printing, followed by 24–48 hour express dispatch across all Lebanese governorates with Cash on Delivery in USD or Whish Money.'}
                </p>
              </div>
            </div>
          </div>

          {/* External Links & Regional Coverage Bar */}
          <div className="pt-6 border-t border-neutral-100 flex flex-wrap items-center justify-between gap-4 text-xs text-neutral-600">
            <div className="flex flex-wrap items-center gap-4">
              <a
                href="https://wa.me/96181773349"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-[#25D366] hover:underline inline-flex items-center gap-1"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                <span>{language === 'ar' ? 'مراسلة الاستوديو مباشرة عبر واتساب' : 'Direct WhatsApp Studio Chat'}</span>
              </a>
              <span>•</span>
              <a
                href="https://www.instagram.com/coveristic"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-[#E21B24] hover:underline"
              >
                {language === 'ar' ? 'تابع أعمالنا على إنستغرام @coveristic' : 'Follow @coveristic on Instagram'}
              </a>
              <span>•</span>
              <a
                href="https://www.google.com/maps/place/Beirut,+Lebanon"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-neutral-700 hover:underline"
              >
                {language === 'ar' ? 'خريطة تغطية التوصيل في بيروت ولبنان' : 'Beirut & Lebanon Delivery Coverage Map'}
              </a>
            </div>
            <div>
              <a
                href={language === 'ar' ? '/' : '/ar/'}
                onClick={(e) => {
                  if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey && e.button === 0) {
                    e.preventDefault();
                    window.location.href = language === 'ar' ? '/' : '/ar/';
                  }
                }}
                className="font-bold text-neutral-900 hover:text-[#E21B24] underline"
              >
                {language === 'ar' ? 'Browse English Storefront (/)' : 'تصفح المتجر باللغة العربية (/ar/)'}
              </a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};
